import moment from "moment";
import { Subscription } from "zen-observable-ts";
import { action, computed, observable } from "mobx";
import { ObservableQuery } from "@apollo/client";
import {
  BookingFinanceSearchItemFragment,
  TimereportKitSupportSearch_Get,
  TimereportKitSupportSearch_GetTotal,
  BookingStatusType,
  SortOrder,
  TimeReportStatus
} from "@graphql";
import { InfiniteScrollSearchManager } from "@search/InfiniteScrollSearchManager";
import { autobind } from "@bonliva-crm/common";
import { BookingKitSupportFilterManager } from "@bookings/BookingSearch/BookingKitSupportFilterManager";
import { getInitialFilter } from "@bookings/BookingSearch/utils";
import { ISearchStore } from "@search";

import { logSubscriptionError } from "@utils/log-subscription-error";
const initFilter = {
  bookingStatus: [
    BookingStatusType.Billed,
    BookingStatusType.Open,
    BookingStatusType.Paid
  ],
  from: null,
  to: moment.utc().add(1, "weeks").endOf("isoWeek").toDate(),
  hasBookingKits: null
};

export class TimereportKitSupportSearchStore implements ISearchStore {
  filterManager: BookingKitSupportFilterManager;
  searchManager: InfiniteScrollSearchManager<BookingFinanceSearchItemFragment>;

  @observable total: number | null = null;
  queryFuncWatch: ObservableQuery<
    TimereportKitSupportSearch_Get.Return,
    TimereportKitSupportSearch_Get.Variables
  >;
  subscription: Subscription;

  constructor() {
    autobind(this);
    this.filterManager = new BookingKitSupportFilterManager(
      { ...getInitialFilter(), ...initFilter },
      this.fetch
    );

    this.searchManager =
      new InfiniteScrollSearchManager<BookingFinanceSearchItemFragment>(this.fetch, true);

    this.searchManager.fetch();
    (window as any).bookingKitSupportSearchStore = this;
  }

  init = () => {
    const queryInput = this.filterManager.queryInput;
    this.subscription?.unsubscribe();

    this.queryFuncWatch = TimereportKitSupportSearch_Get.funcWatch(
      {
        filter: queryInput,
        limit: 50,
        skip: 0,
        sortOrder: SortOrder.Asc
      },
      { fetchPolicy: "network-only" }
    );

    this.subscription = this.queryFuncWatch.subscribe({
      next: ({ data }) => {
        this.searchManager.merge(data.bookingWeeksKitSupport.items);
      },
      error: logSubscriptionError("TimereportKitSupportSearchStore")
    });

    this.total = null;

    TimereportKitSupportSearch_GetTotal.func(
      {
        filter: queryInput
      },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      this.total = data.bookingWeeksTotal.total;
    });
  };

  unsubscribe = () => this.subscription.unsubscribe();

  refetch() {
    this.queryFuncWatch
      .refetch({
        filter: this.filterManager.queryInput,
        limit: this.searchManager.items?.length || 50,
        skip: 0
      })
      .then((result) => {
        if (result.data) {
          this.searchManager.merge(result.data.bookingWeeksKitSupport?.items ?? []);
          this.total = result.data.bookingWeeksKitSupport.total;
        }
      });
  }

  fetch(skip?: number, limit?: number) {
    const _skip = skip == null ? 0 : skip;
    const _limit = limit == null ? 50 : limit;

    const queryInput = this.filterManager?.queryInput;
    const isNew = _skip === 0;
    isNew && this.searchManager.clear();

    if (isNew) {
      this.init();
      return Promise.resolve();
    } else {
      return this.queryFuncWatch.fetchMore({
        variables: { skip: _skip, limit: _limit, filter: queryInput },
        updateQuery: (previousQueryResult, options) => {
          this.total = options.fetchMoreResult.bookingWeeksKitSupport.total;
          return {
            bookingWeeksKitSupport: {
              ...previousQueryResult.bookingWeeksKitSupport,
              ...options.fetchMoreResult.bookingWeeksKitSupport,
              items: [
                ...previousQueryResult.bookingWeeksKitSupport.items,
                ...options.fetchMoreResult.bookingWeeksKitSupport.items
              ]
            },
            bookingWeeksTotal: null
          };
        }
      });
    }
  }

  deleteRowByBookingId = (bookingId: string) => {
    const index = this.searchManager?.items.findIndex(
      (x) => x.BookingRef.NodeID === bookingId
    );
    if (index >= 0) {
      this.searchManager.deleteRow(index);
    }
  };

  updateRowTimeReportStatusByBookingId = (
    bookingId: string,
    timeReportStatus: TimeReportStatus
  ) => {
    const index = this.searchManager?.items.findIndex(
      (x) => x.BookingRef.NodeID === bookingId
    );
    const item = {
      ...this.searchManager?.items[index],
      TimeReportStatus: timeReportStatus
    };
    if (index >= 0) {
      this.searchManager.updateRow(index, item);
    }
  };

  @computed get hasMore() {
    return this.searchManager.rowCount < this.total;
  }

  @action handleRowUpd(index: number, row) {
    this.searchManager.updateRow(index, row);
  }

  @computed get showKitSupport() {
    return true;
  }
}
